import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  SHARE_EXPIRY_OPTIONS,
  fetchActiveShareLink,
  createShareLink,
  revokeShareLink,
  sendShareLink,
} from '../../Services/shareLinks';

/**
 * Create, copy, email and revoke a short lived link — for any resource that
 * registers a share kind on the server. Drop it into a modal or a tab and
 * pass the kind and the resource id; nothing in here is feature specific.
 */

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export default function ShareLinkPanel({
  kind,
  resourceId,
  // What the person on the other end will be able to do, for the warning.
  description = 'open this and download a copy',
  canEmail = true,
  emailLabel = 'Email patient',
  defaultExpiryMinutes = 60,
}) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(defaultExpiryMinutes);

  // Show the link already in circulation rather than silently minting a new
  // one, so the copy the patient is holding keeps working.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setShare(await fetchActiveShareLink(kind, resourceId));
    } catch (error) {
      console.log('Error loading share link:', error);
      toast.error(error?.response?.data?.message || 'Could not load the share link');
    } finally {
      setLoading(false);
    }
  }, [kind, resourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setWorking(true);
    try {
      setShare(await createShareLink(kind, resourceId, { expiryMinutes }));
      toast.success('Share link created');
    } catch (error) {
      console.log('Error creating share link:', error);
      toast.error(error?.response?.data?.message || 'Could not create the share link');
    } finally {
      setWorking(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share.link);
      toast.success('Link copied');
    } catch (error) {
      toast.error('Could not copy the link');
    }
  };

  const email = async () => {
    setWorking(true);
    try {
      const res = await sendShareLink(share._id);
      toast.success(res?.message || 'Link sent');
      await load();
    } catch (error) {
      console.log('Error sending share link:', error);
      toast.error(error?.response?.data?.message || 'Could not send the share link');
    } finally {
      setWorking(false);
    }
  };

  const revoke = async () => {
    setWorking(true);
    try {
      await revokeShareLink(share._id);
      toast.success('Share link revoked');
      setShare(null);
    } catch (error) {
      console.log('Error revoking share link:', error);
      toast.error(error?.response?.data?.message || 'Could not revoke the share link');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <div className="p-4 mb-5 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
        <p className="font-semibold">The link is the password</p>
        <p className="mt-0.5 text-amber-800">
          Anyone holding it can {description}, without logging in. It stops working on its own once it
          expires.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Checking for an existing link...</p>}

      {!loading && share && (
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-bold text-gray-800">Active link</h4>
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-[11px] font-semibold text-emerald-700">
              Expires {fmtDateTime(share.expiresAt)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded bg-gray-100 px-3 py-2 text-xs text-gray-700">
              {share.link}
            </code>
            <button
              onClick={copy}
              className="px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded"
            >
              Copy
            </button>
            <a
              href={share.link}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded"
            >
              Open
            </a>
            {canEmail && (
              <button
                onClick={email}
                disabled={working}
                className="px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
              >
                {emailLabel}
              </button>
            )}
            <button
              onClick={revoke}
              disabled={working}
              className="px-3 py-2 text-xs font-semibold text-red-600 border border-red-300 hover:bg-red-50 rounded disabled:opacity-50"
            >
              Revoke
            </button>
          </div>

          {share.sentTo?.length > 0 && (
            <p className="mt-3 text-xs text-gray-500">Emailed to {share.sentTo.join(', ')}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Opened {share.useCount || 0} time(s)</p>
        </div>
      )}

      {!loading && (
        <div className={`border rounded-lg p-4 ${share ? 'mt-4' : ''}`}>
          <h4 className="text-sm font-bold text-gray-800">
            {share ? 'Issue a new link' : 'Create a share link'}
          </h4>
          <p className="mt-1 text-xs text-gray-500">
            {share
              ? 'The current link stops working immediately — anyone still holding it will need the new one.'
              : `They can ${description} on any phone, with no account and no password.`}
          </p>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Valid for</label>
              <select
                value={expiryMinutes}
                onChange={(e) => setExpiryMinutes(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SHARE_EXPIRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={create}
              disabled={working}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold shadow-sm disabled:opacity-50"
            >
              {working ? 'Working...' : share ? 'Replace link' : 'Create link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
